import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid2 as Grid,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Avatar,
  CircularProgress
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { updateEcom, getVendorPaymentMethods } from "../../services/vendorServices";
import { useVendorIndexPolling } from "../Context/vendorIndexPollingContext";
import { convertToBase64Jpeg } from "../../utils/imageUtils";
import WowOverlay from "../Shared/WowOverlay";
import ActionDialog from "../Shared/ActionDialog";
import { isValidVatNumber, normalizeVatNumber } from "../../utils/vatNumberUtils";
import { validateSftpCatalogUrl } from "../../utils/validators";
import SedeAziendale from "./SedeAziendale";
import VatNumberField from "../Shared/VatNumberField";

const STATUS_OPTIONS = [
  { value: "In Attivazione", label: "Registrato" },
  { value: "Registrato", label: "Verificato" },
  { value: "Attivo", label: "Attivo" },
  { value: "Sospeso", label: "Sospeso" },
  { value: "Non Attivo", label: "Disattivo" }
];

const REGEX_NAME = /^[a-zA-Z0-9àèéìòùÀÈÉÌÒÙ](?:[a-zA-Z0-9àèéìòùÀÈÉÌÒÙ\s'.,&\-/()]{0,254}[a-zA-Z0-9àèéìòùÀÈÉÌÒÙ.])?$/;
const REGEX_EMAIL = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const REGEX_PHONE = /^\d{6,15}$/;
const REGEX_SDI = /^[A-Z\d]{6,7}$/;
const REGEX_PASSWORD = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%^&+=!*]).{8,20}$/;
const REGEX_CF = /^(\d{11}|[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z])$/;

const normalize = (v) => (v === null || v === undefined) ? "" : String(v).trim();

const formatValue = (value) => (value === null || value === undefined || value === "") ? "-" : value;

// Sorgente immagine dal logo base64 (con o senza prefisso data URL)
const vendorLogoSrc = (logo) => {
  if (!logo) return undefined;
  return logo.startsWith("data:") ? logo : `data:image/jpeg;base64,${logo}`;
};

// Confronta due liste di id come insiemi (ordine ininfluente)
const sameIdSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const getStatusLabelFromRaw = (s) => {
  if (!s) return "-";
  const sStr = s.toString().toLowerCase();
  if (s === 1 || s === "1" || sStr === "in attivazione") return "Registrato";
  if (s === 2 || s === "2" || sStr === "registrato") return "Verificato";
  if (s === 3 || s === "3" || sStr === "attivo") return "Attivo";
  if (s === 4 || s === "4" || sStr === "sospeso") return "Sospeso";
  if (s === 5 || s === "5" || sStr === "disattivo" || sStr === "non attivo") return "Disattivo";
  return s.toString();
};

const getRawStatusFromLabel = (label) => {
  const found = STATUS_OPTIONS.find(opt => opt.label === label);
  return found ? found.value : label;
};

const getAvailableStatusOptions = (currentLabel) => {
  const rules = {
    "Verificato": ["Attivo", "Disattivo"],
    "Attivo": ["Sospeso"],
    "Sospeso": ["Attivo", "Disattivo"],
    "Disattivo": ["Attivo"],
    "Registrato": []
  };
  const allowedLabels = rules[currentLabel] || [];
  return STATUS_OPTIONS.filter(opt => opt.label === currentLabel || allowedLabels.includes(opt.label));
};

const validateWebsite = (url) => {
  const testUrl = (!url || url.startsWith("https://") || url.startsWith("http://")) ? url : "https://" + url;
  if (!testUrl) return { isValid: true };
  if (testUrl.length < 7 || testUrl.length > 40) return { isValid: false, message: "Lunghezza URL non valida (7-40 car.)" };
  if (!testUrl.startsWith("https://")) return { isValid: false, message: "Solo protocollo HTTPS supportato" };
  try {
    const urlObj = new URL(testUrl);
    const host = urlObj.hostname;
    if (!host) return { isValid: false, message: "Formato host non valido" };
    const parts = host.split('.');
    if (parts.includes("")) return { isValid: false, message: parts.at(-1) === "" && parts.length > 1 ? "Completare l'estensione del dominio" : "Punti non validi nell'host" };
    const isWww = parts[0].toLowerCase() === "www";
    if (isWww && parts.length < 3) return { isValid: false, message: "Formato www.dominio.ext richiesto" };
    if (!isWww && parts.length < 2) return { isValid: false, message: "Formato dominio.ext richiesto" };
    if (!/^[a-zA-Z]{2,6}$/.test(parts.at(-1))) return { isValid: false, message: "Estensione dominio non valida" };
    return { isValid: true };
  } catch {
    return { isValid: false, message: "URL malformato" };
  }
};

const runValidation = (values) => {
  const flags = {
    isBusinessNameInvalid: values.businessName === "" || !REGEX_NAME.test(values.businessName),
    isTradeNameInvalid: values.tradeName === "" || !REGEX_NAME.test(values.tradeName),
    isTaxCodeInvalid: (values.taxCode.trim() !== "" && !REGEX_CF.test(values.taxCode.trim().toUpperCase())) || (!values.taxCode.trim() && !values.vatNumber.trim()),
    isVatNumberInvalid: (values.vatNumber.trim() !== "" && !isValidVatNumber(values.vatNumber)) || (!values.taxCode.trim() && !values.vatNumber.trim()),
    isPhoneNumberInvalid: values.phoneNumber === "" || !REGEX_PHONE.test(values.phoneNumber),
    isEmailInvalid: values.email === "" || !REGEX_EMAIL.test(values.email),
    isPecInvalid: (values.pec.trim() !== "" && !REGEX_EMAIL.test(values.pec.trim())) || (!values.pec.trim() && !values.sdiCode.trim()),
    isSdiCodeInvalid: (values.sdiCode.trim() !== "" && !REGEX_SDI.test(values.sdiCode.trim().toUpperCase())) || (!values.pec.trim() && !values.sdiCode.trim()),
    isUsernameInvalid: !values.username || values.username.length < 3 || values.username.includes(" "),
    isPasswordInvalid: values.password !== "" && !REGEX_PASSWORD.test(values.password),
    isNameInvalid: values.referringName === "" || !REGEX_NAME.test(values.referringName),
    isSurnameInvalid: values.referringSurname === "" || !REGEX_NAME.test(values.referringSurname),
    isPubEmailInvalid: values.pubEmail !== "" && !REGEX_EMAIL.test(values.pubEmail),
    isRefEmailInvalid: values.refEmail !== "" && !REGEX_EMAIL.test(values.refEmail),
    isRefPhoneNumberInvalid: values.refPhoneNumber !== "" && !REGEX_PHONE.test(values.refPhoneNumber),
  };
  const websiteValidation = validateWebsite(values.websiteUrl);
  flags.isWebsiteInvalid = values.websiteUrl !== "" && !websiteValidation.isValid;
  flags.websiteValidation = websiteValidation;
  const sftpValidation = validateSftpCatalogUrl(values.sftpServerUrl);
  flags.isSftpServerUrlInvalid = values.sftpServerUrl !== "" && !sftpValidation.isValid;
  flags.sftpValidation = sftpValidation;
  return flags;
};

const ModificaEcommerce = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const vendor = location.state?.vendor;
  const { startVendorIndexPolling } = useVendorIndexPolling();

  const [businessName, setBusinessName] = useState(vendor?.tradeName || "");
  const [tradeName, setTradeName] = useState(vendor?.businessName || "");
  const [taxCode, setTaxCode] = useState(vendor?.taxCode || "");
  const [vatNumber, setVatNumber] = useState(vendor?.vatNumber || "");
  const [phoneNumber, setPhoneNumber] = useState(vendor?.phoneNumber || "");
  const [email, setEmail] = useState(vendor?.email || "");
  const [sftpServerUrl, setSftpServerUrl] = useState(vendor?.sftpServerUrl || "");

  const initialAddress = useMemo(() => ({
    via: vendor?.address?.via || "",
    civico: vendor?.address?.civico || "",
    comune: vendor?.address?.comune || "",
    cap: vendor?.address?.cap || "",
    provincia: vendor?.address?.provincia || "",
    regione: vendor?.address?.regione || "",
    nazione: vendor?.address?.nazione || "",
    indirizzo: vendor?.address?.indirizzo || ""
  }), [vendor]);

  const [sedeAddressValues, setSedeAddressValues] = useState(initialAddress);
  const [verifiedSedeSnapshot, setVerifiedSedeSnapshot] = useState(initialAddress);

  const [pec, setPec] = useState(vendor?.pec || "");
  const [sdiCode, setSdiCode] = useState(vendor?.sdiCode || "");
  const [username, setUsername] = useState(vendor?.username || "");
  const [password, setPassword] = useState("");
  const [referringName, setReferringName] = useState(vendor?.referringName || "");
  const [referringSurname, setReferringSurname] = useState(vendor?.referringSurname || "");
  const [websiteUrl, setWebsiteUrl] = useState(vendor?.websiteUrl || "");
  const [pubEmail, setPubEmail] = useState(vendor?.pubEmail || "");
  const [refEmail, setRefEmail] = useState(vendor?.refEmail || "");
  const [refPhoneNumber, setRefPhoneNumber] = useState(vendor?.refPhoneNumber || "");
  const [selectedStatus, setSelectedStatus] = useState(getStatusLabelFromRaw(vendor?.stato));
  const [showPassword, setShowPassword] = useState(false);

  const [logo, setLogo] = useState(vendor?.logo || "");
  const [logoError, setLogoError] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [initialPaymentIds, setInitialPaymentIds] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (!vendor) navigate("/gestione-ecommerce");
  }, [vendor, navigate]);

  // Carica i metodi di pagamento disponibili e quelli già associati al vendor
  useEffect(() => {
    if (!vendor) return undefined;
    let active = true;
    const loadPaymentMethods = async () => {
      setPaymentLoading(true);
      setPaymentError("");
      try {
        const data = await getVendorPaymentMethods(vendor.vendorListId);
        if (!active) return;
        const methods = data || [];
        const selected = methods.filter((m) => m.associated).map((m) => m.id);
        setPaymentMethods(methods);
        setSelectedPaymentIds(selected);
        setInitialPaymentIds(selected);
      } catch (err) {
        if (active) setPaymentError(err.message);
      } finally {
        if (active) setPaymentLoading(false);
      }
    };
    loadPaymentMethods();
    return () => { active = false; };
  }, [vendor]);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permette di ricaricare lo stesso file
    if (!file) return;
    setLogoError("");
    try {
      const base64 = await convertToBase64Jpeg(file);
      setLogo(base64);
    } catch {
      setLogoError("Impossibile elaborare l'immagine selezionata.");
    }
  };

  const togglePaymentMethod = (id) => {
    setSelectedPaymentIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const currentState = useMemo(() => ({
    businessName, tradeName, taxCode, vatNumber, phoneNumber, email, pec, sdiCode, username, password, referringName, referringSurname, websiteUrl,
    sftpServerUrl, pubEmail, refEmail, refPhoneNumber, sedeAddressValues
  }), [businessName, tradeName, taxCode, vatNumber, phoneNumber, email, pec, sdiCode, username, password, referringName, referringSurname, websiteUrl, sftpServerUrl, pubEmail, refEmail, refPhoneNumber, sedeAddressValues]);

  const validationFlags = useMemo(() => runValidation(currentState), [currentState]);

  const initialValidationFlags = useMemo(() => {
    if (!vendor) return {};
    return runValidation({
      businessName: vendor.tradeName || "", tradeName: vendor.businessName || "", taxCode: vendor.taxCode || "",
      vatNumber: vendor.vatNumber || "", phoneNumber: vendor.phoneNumber || "", email: vendor.email || "",
      pec: vendor.pec || "", sdiCode: vendor.sdiCode || "", username: vendor.username || "",
      password: "", referringName: vendor.referringName || "", referringSurname: vendor.referringSurname || "",
      websiteUrl: vendor.websiteUrl || "", sftpServerUrl: vendor.sftpServerUrl || "",
      pubEmail: vendor.pubEmail || "", refEmail: vendor.refEmail || "", refPhoneNumber: vendor.refPhoneNumber || ""
    });
  }, [vendor]);

  const {
    isBusinessNameInvalid, isTradeNameInvalid, isTaxCodeInvalid, isVatNumberInvalid,
    isPhoneNumberInvalid, isEmailInvalid, isPecInvalid, isSdiCodeInvalid,
    isUsernameInvalid, isPasswordInvalid, isNameInvalid, isSurnameInvalid,
    isWebsiteInvalid, websiteValidation, isSftpServerUrlInvalid, sftpValidation, isPubEmailInvalid, isRefEmailInvalid, isRefPhoneNumberInvalid
  } = validationFlags;

  const fieldChanges = useMemo(() => ({
    businessName: normalize(businessName) !== normalize(vendor?.tradeName),
    tradeName: normalize(tradeName) !== normalize(vendor?.businessName),
    taxCode: normalize(taxCode) !== normalize(vendor?.taxCode),
    vatNumber: normalize(vatNumber) !== normalize(vendor?.vatNumber),
    phoneNumber: normalize(phoneNumber) !== normalize(vendor?.phoneNumber),
    email: normalize(email) !== normalize(vendor?.email),
    sftpServerUrl: normalize(sftpServerUrl) !== normalize(vendor?.sftpServerUrl),
    indirizzo: normalize(sedeAddressValues.indirizzo) !== normalize(vendor?.address?.indirizzo),
    via: normalize(sedeAddressValues.via) !== normalize(vendor?.address?.via),
    civico: normalize(sedeAddressValues.civico) !== normalize(vendor?.address?.civico),
    comune: normalize(sedeAddressValues.comune) !== normalize(vendor?.address?.comune),
    cap: normalize(sedeAddressValues.cap) !== normalize(vendor?.address?.cap),
    provincia: normalize(sedeAddressValues.provincia) !== normalize(vendor?.address?.provincia),
    regione: normalize(sedeAddressValues.regione) !== normalize(vendor?.address?.regione),
    nazione: normalize(sedeAddressValues.nazione) !== normalize(vendor?.address?.nazione),
    pec: normalize(pec) !== normalize(vendor?.pec),
    sdiCode: normalize(sdiCode) !== normalize(vendor?.sdiCode),
    username: normalize(username) !== normalize(vendor?.username),
    password: password !== "",
    referringName: normalize(referringName) !== normalize(vendor?.referringName),
    referringSurname: normalize(referringSurname) !== normalize(vendor?.referringSurname),
    websiteUrl: normalize(websiteUrl) !== normalize(vendor?.websiteUrl),
    pubEmail: normalize(pubEmail) !== normalize(vendor?.pubEmail),
    refEmail: normalize(refEmail) !== normalize(vendor?.refEmail),
    refPhoneNumber: normalize(refPhoneNumber) !== normalize(vendor?.refPhoneNumber),
    selectedStatus: selectedStatus !== getStatusLabelFromRaw(vendor?.stato),
    logo: normalize(logo) !== normalize(vendor?.logo),
    paymentMethods: !sameIdSet(selectedPaymentIds, initialPaymentIds)
  }), [businessName, tradeName, taxCode, vatNumber, phoneNumber, email, sftpServerUrl, sedeAddressValues, pec, sdiCode, username, password, referringName, referringSurname, websiteUrl, pubEmail, refEmail, refPhoneNumber, selectedStatus, logo, selectedPaymentIds, initialPaymentIds, vendor]);

  const otherFieldsHelperText = (isInvalid, isModified, isInitiallyInvalid, defaultMsg) => {
    if (!isInvalid) return "";
    return (isModified || !isInitiallyInvalid) ? defaultMsg : `${defaultMsg} (Pre-esistente)`;
  };

  const getPairHelperText = ({
    isInvalid, isSpecificModified, isPairModified, isInitiallyInvalid, valA, valB, formatMsg, pairMsg
  }) => {
    if (!isInvalid) return "";
    let msg = "";
    let modified = isSpecificModified;
    if (!normalize(valA) && !normalize(valB)) {
      msg = pairMsg;
      modified = isPairModified;
    } else {
      msg = formatMsg;
    }
    return (msg && !modified && isInitiallyInvalid) ? `${msg} (Pre-esistente)` : msg;
  };

  const taxCodeHelperText = useMemo(() => getPairHelperText({
    isInvalid: isTaxCodeInvalid, isSpecificModified: fieldChanges.taxCode, isPairModified: fieldChanges.taxCode || fieldChanges.vatNumber,
    isInitiallyInvalid: initialValidationFlags.isTaxCodeInvalid, valA: taxCode, valB: vatNumber,
    formatMsg: "Codice Fiscale Aziendale non valido", pairMsg: "Almeno uno tra Partita IVA e Codice Fiscale deve essere compilato"
  }), [isTaxCodeInvalid, fieldChanges, initialValidationFlags, taxCode, vatNumber]);

  const vatNumberHelperText = useMemo(() => getPairHelperText({
    isInvalid: isVatNumberInvalid, isSpecificModified: fieldChanges.vatNumber, isPairModified: fieldChanges.taxCode || fieldChanges.vatNumber,
    isInitiallyInvalid: initialValidationFlags.isVatNumberInvalid, valA: taxCode, valB: vatNumber,
    formatMsg: "Partita IVA non valida", pairMsg: "Almeno uno tra Partita IVA e Codice Fiscale deve essere compilato"
  }), [isVatNumberInvalid, fieldChanges, initialValidationFlags, taxCode, vatNumber]);

  const pecHelperText = useMemo(() => getPairHelperText({
    isInvalid: isPecInvalid, isSpecificModified: fieldChanges.pec, isPairModified: fieldChanges.pec || fieldChanges.sdiCode,
    isInitiallyInvalid: initialValidationFlags.isPecInvalid, valA: pec, valB: sdiCode,
    formatMsg: "Email PEC non valida", pairMsg: "Almeno uno tra PEC e Codice SDI deve essere compilato"
  }), [isPecInvalid, fieldChanges, initialValidationFlags, pec, sdiCode]);

  const sdiCodeHelperText = useMemo(() => getPairHelperText({
    isInvalid: isSdiCodeInvalid, isSpecificModified: fieldChanges.sdiCode, isPairModified: fieldChanges.pec || fieldChanges.sdiCode,
    isInitiallyInvalid: initialValidationFlags.isSdiCodeInvalid, valA: pec, valB: sdiCode,
    formatMsg: "Codice SDI non valido (6-7 caratteri alfanumerici)", pairMsg: "Almeno uno tra PEC e Codice SDI deve essere compilato"
  }), [isSdiCodeInvalid, fieldChanges, initialValidationFlags, pec, sdiCode]);

  const isAnyBlockingFieldInvalid = useMemo(() => {
    const basicChecks = [
      { modified: fieldChanges.businessName, invalid: isBusinessNameInvalid },
      { modified: fieldChanges.tradeName, invalid: isTradeNameInvalid },
      { modified: fieldChanges.phoneNumber, invalid: isPhoneNumberInvalid },
      { modified: fieldChanges.email, invalid: isEmailInvalid },
      { modified: fieldChanges.username, invalid: isUsernameInvalid },
      { modified: fieldChanges.password, invalid: isPasswordInvalid },
      { modified: fieldChanges.referringName, invalid: isNameInvalid },
      { modified: fieldChanges.referringSurname, invalid: isSurnameInvalid },
      { modified: fieldChanges.websiteUrl, invalid: isWebsiteInvalid },
      { modified: fieldChanges.sftpServerUrl, invalid: isSftpServerUrlInvalid },
      { modified: fieldChanges.pubEmail, invalid: isPubEmailInvalid },
      { modified: fieldChanges.refEmail, invalid: isRefEmailInvalid },
      { modified: fieldChanges.refPhoneNumber, invalid: isRefPhoneNumberInvalid }
    ];

    if (basicChecks.some(c => c.modified && c.invalid)) return true;

    const checkTaxVat = () => {
      const vTax = normalize(taxCode);
      const vVat = normalize(vatNumber);
      if (fieldChanges.taxCode && vTax !== "" && !REGEX_CF.test(vTax.toUpperCase())) return true;
      if (fieldChanges.vatNumber && vVat !== "" && !isValidVatNumber(vVat)) return true;
      if ((fieldChanges.taxCode || fieldChanges.vatNumber) && !vTax && !vVat && !(!vendor?.taxCode && !vendor?.vatNumber)) return true;
      return false;
    };

    if (checkTaxVat()) return true;

    const checkPecSdi = () => {
      const vPec = normalize(pec);
      const vSdi = normalize(sdiCode);
      if (fieldChanges.pec && vPec !== "" && !REGEX_EMAIL.test(vPec)) return true;
      if (fieldChanges.sdiCode && vSdi !== "" && !REGEX_SDI.test(vSdi.toUpperCase())) return true;
      if ((fieldChanges.pec || fieldChanges.sdiCode) && !vPec && !vSdi && !(!vendor?.pec && !vendor?.sdiCode)) return true;
      return false;
    };

    if (checkPecSdi()) return true;

    // Address verification check
    const addressModified = ["via", "civico", "comune", "cap", "provincia", "regione", "nazione"].some(k => normalize(sedeAddressValues[k]) !== normalize(verifiedSedeSnapshot[k]));
    if (addressModified) return true;

    return false;
  }, [isBusinessNameInvalid, isTradeNameInvalid, isPhoneNumberInvalid, isEmailInvalid, isUsernameInvalid, isPasswordInvalid, isNameInvalid, isSurnameInvalid, isWebsiteInvalid, isPubEmailInvalid, isRefEmailInvalid, isRefPhoneNumberInvalid, fieldChanges, vendor, taxCode, vatNumber, pec, sdiCode, sedeAddressValues, verifiedSedeSnapshot]);

  const isSaveDisabled = !Object.values(fieldChanges).some(Boolean) || isAnyBlockingFieldInvalid;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionReason, setActionReason] = useState("");
  const [sendEmailChecked, setSendEmailChecked] = useState(true);
  const [includeReasonChecked, setIncludeReasonChecked] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInitialReset = () => {
    setBusinessName(vendor?.tradeName || "");
    setTradeName(vendor?.businessName || "");
    setTaxCode(vendor?.taxCode || "");
    setVatNumber(vendor?.vatNumber || "");
    setPhoneNumber(vendor?.phoneNumber || "");
    setEmail(vendor?.email || "");
    setSftpServerUrl(vendor?.sftpServerUrl || "");
    setSedeAddressValues(initialAddress);
    setVerifiedSedeSnapshot(initialAddress);
    setPec(vendor?.pec || "");
    setSdiCode(vendor?.sdiCode || "");
    setUsername(vendor?.username || "");
    setPassword("");
    setReferringName(vendor?.referringName || "");
    setReferringSurname(vendor?.referringSurname || "");
    setWebsiteUrl(vendor?.websiteUrl || "");
    setPubEmail(vendor?.pubEmail || "");
    setRefEmail(vendor?.refEmail || "");
    setRefPhoneNumber(vendor?.refPhoneNumber || "");
    setSelectedStatus(getStatusLabelFromRaw(vendor?.stato));
    setLogo(vendor?.logo || "");
    setLogoError("");
    setSelectedPaymentIds(initialPaymentIds);
  };

  const handleSave = async (overrides = {}) => {
    try {
      setIsModalOpen(false);
      setErrorMessage("");
      setUpdateStatus('loading');
      const isForced = overrides.forcedEmail !== undefined;
      const finalEmail = isForced ? overrides.forcedEmail : sendEmailChecked;
      const finalReason = isForced ? overrides.forcedReason : actionReason;
      const finalIncludeReason = isForced ? true : includeReasonChecked;
      const rawStatus = getRawStatusFromLabel(selectedStatus);
      let fWeb = websiteUrl;
      if (fWeb && !fWeb.startsWith("https://") && !fWeb.startsWith("http://")) fWeb = "https://" + fWeb;

      const getVal = (val, original, isInitInvalid) => {
        const v = normalize(val);
        const o = normalize(original);
        if (v === "") return null;
        if (v === o && isInitInvalid) return null;
        return val;
      };

      const payload = {
        idVendor: vendor.vendorListId, sendEmail: finalEmail, reason: finalReason, includeReason: finalIncludeReason, stato: rawStatus,
        businessName: getVal(tradeName, vendor.businessName, initialValidationFlags.isTradeNameInvalid),
        tradeName: getVal(businessName, vendor.tradeName, initialValidationFlags.isBusinessNameInvalid),
        taxCode: getVal(taxCode, vendor.taxCode, initialValidationFlags.isTaxCodeInvalid),
        vatNumber: (() => {
          const raw = getVal(vatNumber, vendor.vatNumber, initialValidationFlags.isVatNumberInvalid);
          return raw == null || raw === "" ? raw : normalizeVatNumber(raw);
        })(),
        phoneNumber: getVal(phoneNumber, vendor.phoneNumber, initialValidationFlags.isPhoneNumberInvalid),
        email: getVal(email, vendor.email, initialValidationFlags.isEmailInvalid),
        sftpServerUrl: getVal(sftpServerUrl, vendor.sftpServerUrl, initialValidationFlags.isSftpServerUrlInvalid),
        indirizzo: getVal(sedeAddressValues.indirizzo, vendor.address?.indirizzo, false),
        via: getVal(sedeAddressValues.via, vendor.address?.via, false),
        civico: getVal(sedeAddressValues.civico, vendor.address?.civico, false),
        cap: getVal(sedeAddressValues.cap, vendor.address?.cap, false),
        comune: getVal(sedeAddressValues.comune, vendor.address?.comune, false),
        provincia: getVal(sedeAddressValues.provincia, vendor.address?.provincia, false),
        regione: getVal(sedeAddressValues.regione, vendor.address?.regione, false),
        nazione: getVal(sedeAddressValues.nazione, vendor.address?.nazione, false),
        pec: getVal(pec, vendor.pec, initialValidationFlags.isPecInvalid),
        sdiCode: getVal(sdiCode, vendor.sdiCode, initialValidationFlags.isSdiCodeInvalid),
        username: getVal(username, vendor.username, initialValidationFlags.isUsernameInvalid),
        password: password === "" ? null : password,
        referringName: getVal(referringName, vendor.referringName, initialValidationFlags.isNameInvalid),
        referringSurname: getVal(referringSurname, vendor.referringSurname, initialValidationFlags.isSurnameInvalid),
        websiteUrl: getVal(fWeb, vendor.websiteUrl, initialValidationFlags.isWebsiteInvalid),
        pubEmail: getVal(pubEmail, vendor.pubEmail, initialValidationFlags.isPubEmailInvalid),
        refEmail: getVal(refEmail, vendor.refEmail, initialValidationFlags.isRefEmailInvalid),
        refPhoneNumber: getVal(refPhoneNumber, vendor.refPhoneNumber, initialValidationFlags.isRefPhoneNumberInvalid),
        logo: fieldChanges.logo ? logo : null,
        paymentMethodIds: fieldChanges.paymentMethods ? selectedPaymentIds : null
      };

      const statusChanged = selectedStatus !== getStatusLabelFromRaw(vendor?.stato);

      await updateEcom(payload);
      if (statusChanged) {
        startVendorIndexPolling();
      }
      setUpdateStatus('success');
      setShowOverlay(true);
      await new Promise(r => setTimeout(r, 1500));
      setShowOverlay(false);
      navigate("/gestione-ecommerce");
    } catch (err) {
      setErrorMessage(err.message);
      setUpdateStatus('error');
      setShowOverlay(true);
      await new Promise(r => setTimeout(r, 3500));
      setShowOverlay(false);
      setUpdateStatus(null);
      setErrorMessage("");
    }
  };

  if (!vendor) return null;

  // Render dei metodi di pagamento in base a stato di caricamento/errore
  const renderPaymentMethods = () => {
    if (paymentLoading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
    }
    if (paymentError) {
      return <Typography variant="body2" color="error">{paymentError}</Typography>;
    }
    if (paymentMethods.length === 0) {
      return <Typography variant="body2" color="text.secondary">Nessun metodo di pagamento disponibile.</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {paymentMethods.map((m) => {
          const selected = selectedPaymentIds.includes(m.id);
          return (
            <Box
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => togglePaymentMethod(m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePaymentMethod(m.id); } }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                border: selected ? '2px solid #0b3d87' : '1px solid #d0d7de',
                backgroundColor: selected ? 'rgba(11,61,135,0.06)' : '#fff',
                opacity: selected ? 1 : 0.65,
                transition: 'all 0.15s',
                "&:hover": { borderColor: '#0b3d87', opacity: 1 }
              }}
            >
              {m.urlLogo && (
                <Box component="img" src={m.urlLogo} alt={m.descrizione} sx={{ width: 36, height: 24, objectFit: 'contain' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500, color: selected ? '#0b3d87' : '#475569' }}>
                {m.descrizione}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/gestione-ecommerce")} sx={{ color: "#0b3d87", fontWeight: "bold", textTransform: "none", "&:hover": { backgroundColor: "transparent", opacity: 0.8 } }}>
          Torna a Gestione Ecommerce
        </Button>
      </Box>

      <Paper elevation={4} sx={{ p: 4, borderRadius: 3, maxWidth: "1000px", mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 1 }}>Modifica E-Commerce</Typography>
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>ID Vendor: {vendor.vendorListId} | {vendor.businessName}</Typography>
          </Box>
          <Chip label={getStatusLabelFromRaw(vendor.stato)} variant="filled" sx={{ fontWeight: "bold", backgroundColor: "#e3f2fd", color: "#0b3d87", px: 1 }} />
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#0b3d87", borderRadius: 1 }} />Dati Societari & Fiscali
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Ragione Sociale" fullWidth value={businessName} onChange={(e) => setBusinessName(e.target.value)} error={isBusinessNameInvalid} helperText={otherFieldsHelperText(isBusinessNameInvalid, fieldChanges.businessName, initialValidationFlags.isBusinessNameInvalid, "Formato ragione sociale non valido")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Nome Commerciale" fullWidth value={tradeName} onChange={(e) => setTradeName(e.target.value)} error={isTradeNameInvalid} helperText={otherFieldsHelperText(isTradeNameInvalid, fieldChanges.tradeName, initialValidationFlags.isTradeNameInvalid, "Formato nome commerciale non valido")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <VatNumberField
                  variant="admin"
                  name="vatNumber"
                  label="Partita IVA"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  error={isVatNumberInvalid}
                  helperText={vatNumberHelperText}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Codice Fiscale Aziendale" fullWidth value={taxCode} onChange={(e) => setTaxCode(e.target.value)} error={isTaxCodeInvalid} helperText={taxCodeHelperText} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Codice SDI" fullWidth value={sdiCode} onChange={(e) => setSdiCode(e.target.value)} error={isSdiCodeInvalid} helperText={sdiCodeHelperText} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Budget Attuale" fullWidth value={`${formatValue(vendor.importo)} €`} disabled sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#8d6e63", fontWeight: "600" }, "& .MuiInputBase-root.Mui-disabled": { backgroundColor: "#f1f1f1" } }} /></Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#0b3d87", borderRadius: 1 }} />Recapiti & Sede Legale
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Email Principale" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} error={isEmailInvalid} helperText={otherFieldsHelperText(isEmailInvalid, fieldChanges.email, initialValidationFlags.isEmailInvalid, "Email non valida")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Email Pubblica" fullWidth value={pubEmail} onChange={(e) => setPubEmail(e.target.value)} error={isPubEmailInvalid} helperText={otherFieldsHelperText(isPubEmailInvalid, fieldChanges.pubEmail, initialValidationFlags.isPubEmailInvalid, "Email pubblica non valida")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Telefono" fullWidth value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} error={isPhoneNumberInvalid} helperText={otherFieldsHelperText(isPhoneNumberInvalid, fieldChanges.phoneNumber, initialValidationFlags.isPhoneNumberInvalid, "Telefono non valido (max 10 cifre)")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="PEC" fullWidth value={pec} onChange={(e) => setPec(e.target.value)} error={isPecInvalid} helperText={pecHelperText} /></Grid>
              <Grid size={{ xs: 12 }}>
                <SedeAziendale
                  enableValidation={true}
                  value={sedeAddressValues}
                  onSedeFieldChange={(k, v) => setSedeAddressValues(prev => ({ ...prev, [k]: v }))}
                  hasStoredAddress={!!vendor?.address?.via}
                  addressNeedsReverify={["via", "civico", "comune", "cap", "provincia", "regione", "nazione"].some(k => normalize(sedeAddressValues[k]) !== normalize(verifiedSedeSnapshot[k]))}
                  onVerifiedMerge={(normalized) => {
                    setSedeAddressValues(normalized);
                    setVerifiedSedeSnapshot(normalized);
                  }}
                  onResetToVerified={() => {
                    setSedeAddressValues(verifiedSedeSnapshot);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#0b3d87", borderRadius: 1 }} />Configurazione & Credenziali
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="SFTP Server URL" fullWidth value={sftpServerUrl} onChange={(e) => setSftpServerUrl(e.target.value)} error={isSftpServerUrlInvalid} helperText={otherFieldsHelperText(isSftpServerUrlInvalid, fieldChanges.sftpServerUrl, initialValidationFlags.isSftpServerUrlInvalid, isSftpServerUrlInvalid ? (sftpValidation.message || "Formato URL catalogo non valido") : "")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Sito Web (Website URL)" fullWidth value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} error={isWebsiteInvalid} helperText={otherFieldsHelperText(isWebsiteInvalid, fieldChanges.websiteUrl, initialValidationFlags.isWebsiteInvalid, isWebsiteInvalid ? (websiteValidation.message || "Formato URL non valido") : "")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Username" fullWidth value={username} onChange={(e) => setUsername(e.target.value)} error={isUsernameInvalid} helperText={otherFieldsHelperText(isUsernameInvalid, fieldChanges.username, initialValidationFlags.isUsernameInvalid, "Minimo 3 caratteri")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={isPasswordInvalid}
                  helperText={otherFieldsHelperText(isPasswordInvalid, fieldChanges.password, initialValidationFlags.isPasswordInvalid, "8-20 caratteri, Almeno 1 maiu, 1 minu, 1 num, 1 spec")}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="nascondi o mostra password"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#0b3d87", borderRadius: 1 }} />Logo & Metodi di Pagamento
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                  <Avatar
                    variant="rounded"
                    src={vendorLogoSrc(logo)}
                    alt="Logo E-Commerce"
                    sx={{ width: 120, height: 120, bgcolor: '#f1f1f1', color: '#90a4ae', fontSize: '0.75rem', textAlign: 'center' }}
                  >
                    Nessun logo
                  </Avatar>
                  <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none', borderRadius: 2, color: '#0b3d87', borderColor: '#0b3d87', "&:hover": { borderColor: '#1e62c9', backgroundColor: 'rgba(11,61,135,0.04)' } }}>
                    Carica logo<input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                  </Button>
                  {logoError && <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>{logoError}</Typography>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Seleziona i metodi di pagamento accettati da questo e-commerce.
                </Typography>
                {renderPaymentMethods()}
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 18, bgcolor: "#0b3d87", borderRadius: 1 }} />Informazioni Referente & Stato
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Nome Referente" fullWidth value={referringName} onChange={(e) => setReferringName(e.target.value)} error={isNameInvalid} helperText={otherFieldsHelperText(isNameInvalid, fieldChanges.referringName, initialValidationFlags.isNameInvalid, "Formato nome non valido")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Cognome Referente" fullWidth value={referringSurname} onChange={(e) => setReferringSurname(e.target.value)} error={isSurnameInvalid} helperText={otherFieldsHelperText(isSurnameInvalid, fieldChanges.referringSurname, initialValidationFlags.isSurnameInvalid, "Formato cognome non valido")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Email Referente" fullWidth value={refEmail} onChange={(e) => setRefEmail(e.target.value)} error={isRefEmailInvalid} helperText={otherFieldsHelperText(isRefEmailInvalid, fieldChanges.refEmail, initialValidationFlags.isRefEmailInvalid, "Email referente non valida")} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Telefono Referente" fullWidth value={refPhoneNumber} onChange={(e) => setRefPhoneNumber(e.target.value)} error={isRefPhoneNumberInvalid} helperText={otherFieldsHelperText(isRefPhoneNumberInvalid, fieldChanges.refPhoneNumber, initialValidationFlags.isRefPhoneNumberInvalid, "Telefono referente non valido")} /></Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Stato E-Commerce</InputLabel>
                  <Select labelId="status-select-label" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Stato E-Commerce" sx={{ fontWeight: "500", color: "#0b3d87" }} disabled={getAvailableStatusOptions(getStatusLabelFromRaw(vendor.stato)).length <= 1}>
                    {getAvailableStatusOptions(getStatusLabelFromRaw(vendor.stato)).map((opt) => (<MenuItem key={opt.value} value={opt.label}>{opt.label}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 5 }}>
          <Button variant="outlined" sx={{ px: 4, borderRadius: 2, textTransform: "none", color: "#e00036", borderColor: "#e00036", "&:hover": { borderColor: "#c4002f", backgroundColor: "rgba(224, 0, 54, 0.04)" } }} onClick={handleInitialReset}>Annulla Modifiche</Button>
          <Button variant="contained" sx={{ backgroundColor: "#0b3d87", px: 6, borderRadius: 2, textTransform: "none", fontWeight: "bold", "&:hover": { backgroundColor: "#1e62c9" } }} onClick={() => setIsModalOpen(true)} disabled={isSaveDisabled}>Salva Dettagli</Button>
        </Box>
      </Paper>

      <ActionDialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSave}
        title="Conferma Aggiornamento E-Commerce"
        vendorName={vendor.businessName}
        actionSummary={
          <Box>
            {selectedStatus !== getStatusLabelFromRaw(vendor.stato) && (<Typography variant="body1" sx={{ mb: 1 }}>Nuovo Stato: <strong>{selectedStatus}</strong></Typography>)}
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic", mt: 1, display: "block", lineHeight: 1.4 }}>Nota: I campi lasciati vuoti verranno inviati come null e non sovrascriveranno i dati esistenti nel sistema.</Typography>
          </Box>
        }
        reason={actionReason}
        onReasonChange={setActionReason}
        emailChecked={sendEmailChecked}
        onEmailChange={setSendEmailChecked}
        includeReasonChecked={includeReasonChecked}
        onIncludeReasonChange={setIncludeReasonChecked}
        confirmText="Conferma e Salva"
        confirmColor="#0b3d87"
      />
      <WowOverlay open={showOverlay} state={updateStatus} errorMessage={errorMessage || (updateStatus === 'success' ? "Operazione riuscita!" : "Si è verificato un errore!")} />
    </Box>
  );
};

export default ModificaEcommerce;
 